import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { getUsers, getDepartments } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';
import { Plus, MoreHorizontal, Key, UserX, UserCheck, Pencil, Trash2, Loader2, Shield, Clock, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const ROLES = [
  { value: 'super_admin', label: 'Super Admin', color: 'bg-purple-100 text-purple-700' },
  { value: 'department_head', label: 'Abteilungsleiter', color: 'bg-blue-100 text-blue-700' },
  { value: 'deputy', label: 'Stellvertreter', color: 'bg-cyan-100 text-cyan-700' },
  { value: 'user', label: 'Benutzer', color: 'bg-gray-100 text-gray-700' }
];

const safeDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

const formatDate = (value) => {
  const d = safeDate(value);
  return d ? format(d, 'dd.MM.yyyy HH:mm', { locale: de }) : '-';
};

const Users = () => {
  const { isSuperAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    full_name: '',
    password: '',
    role: 'user',
    department_id: ''
  });

  const [passwordData, setPasswordData] = useState({
    password: '',
    confirmPassword: '',
    mustChangePassword: false
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersData, deptsData] = await Promise.all([
        getUsers(),
        getDepartments()
      ]);
      setUsers(usersData);
      setDepartments(deptsData);
    } catch (error) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.username || !formData.password) {
      toast.error('Benutzername und Passwort erforderlich');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/users`, formData, { headers: getAuthHeader() });
      toast.success('Benutzer erstellt');
      setDialogOpen(false);
      setFormData({ username: '', email: '', full_name: '', password: '', role: 'user', department_id: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedUser) return;
    
    setSaving(true);
    try {
      const updateData = {
        email: formData.email,
        full_name: formData.full_name,
        role: formData.role,
        department_id: formData.department_id || null
      };
      await axios.put(`${API_URL}/api/users/${selectedUser.id}`, updateData, { headers: getAuthHeader() });
      toast.success('Benutzer aktualisiert');
      setDialogOpen(false);
      setSelectedUser(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (user) => {
    try {
      const response = await axios.put(`${API_URL}/api/users/${user.id}/toggle-status`, {}, { headers: getAuthHeader() });
      toast.success(response.data.message);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Statuswechsel');
    }
  };

  const handleResetPassword = async (user) => {
    if (!window.confirm(`Passwort für "${user.username}" zurücksetzen?`)) return;
    
    try {
      const response = await axios.put(`${API_URL}/api/users/${user.id}/reset-password`, {}, { headers: getAuthHeader() });
      toast.success(`Neues Passwort: ${response.data.new_password}`, { duration: 10000 });
    } catch (error) {
      toast.error('Fehler beim Zurücksetzen');
    }
  };

  const handleSetPassword = async () => {
    if (!selectedUser) return;
    
    if (passwordData.password !== passwordData.confirmPassword) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }
    
    if (passwordData.password.length < 6) {
      toast.error('Passwort muss mindestens 6 Zeichen haben');
      return;
    }
    
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/users/${selectedUser.id}/set-password`, {
        password: passwordData.password,
        must_change_password: passwordData.mustChangePassword
      }, { headers: getAuthHeader() });
      toast.success('Passwort geändert');
      setPasswordDialogOpen(false);
      setSelectedUser(null);
      setPasswordData({ password: '', confirmPassword: '', mustChangePassword: false });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Ändern');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Benutzer "${user.username}" wirklich löschen?`)) return;
    
    try {
      await axios.delete(`${API_URL}/api/users/${user.id}`, { headers: getAuthHeader() });
      toast.success('Benutzer gelöscht');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setFormData({
      username: user.username,
      email: user.email || '',
      full_name: user.full_name || '',
      password: '',
      role: user.role,
      department_id: user.department_id || ''
    });
    setDialogOpen(true);
  };

  const openPasswordDialog = (user) => {
    setSelectedUser(user);
    setPasswordData({ password: '', confirmPassword: '', mustChangePassword: false });
    setPasswordDialogOpen(true);
  };

  const getRoleBadge = (role) => {
    const roleConfig = ROLES.find(r => r.value === role) || ROLES[3];
    return <Badge className={roleConfig.color}>{roleConfig.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Benutzer</h1>
          <p className="text-gray-500 mt-1">{users.length} Benutzer im System</p>
        </div>
        {isSuperAdmin() && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setSelectedUser(null); }}>
            <DialogTrigger asChild>
              <Button onClick={() => { setSelectedUser(null); setFormData({ username: '', email: '', full_name: '', password: '', role: 'user', department_id: '' }); }}>
                <Plus className="w-4 h-4 mr-2" />
                Neuer Benutzer
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{selectedUser ? 'Benutzer bearbeiten' : 'Neuer Benutzer'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Benutzername *</Label>
                  <Input
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    disabled={!!selectedUser}
                    placeholder="max.mustermann"
                  />
                </div>
                <div>
                  <Label>Vollständiger Name</Label>
                  <Input
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    placeholder="Max Mustermann"
                  />
                </div>
                <div>
                  <Label>E-Mail</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="max@example.com"
                  />
                </div>
                {!selectedUser && (
                  <div>
                    <Label>Passwort *</Label>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="••••••••"
                    />
                  </div>
                )}
                <div>
                  <Label>Rolle</Label>
                  <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ROLES.map((role) => (
                        <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Abteilung</Label>
                  <Select value={formData.department_id || '__none__'} onValueChange={(v) => setFormData({ ...formData, department_id: v === '__none__' ? '' : v })}>
                    <SelectTrigger><SelectValue placeholder="Keine" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Keine Abteilung</SelectItem>
                      {departments.map((dept) => (
                        <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
                <Button onClick={selectedUser ? handleUpdate : handleCreate} disabled={saving}>
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {selectedUser ? 'Speichern' : 'Erstellen'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Password Change Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Passwort ändern</DialogTitle>
            <DialogDescription>
              Neues Passwort für {selectedUser?.username} festlegen
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Neues Passwort</Label>
              <Input
                type="password"
                value={passwordData.password}
                onChange={(e) => setPasswordData({ ...passwordData, password: e.target.value })}
                placeholder="••••••••"
              />
            </div>
            <div>
              <Label>Passwort bestätigen</Label>
              <Input
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                placeholder="••••••••"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={passwordData.mustChangePassword}
                onCheckedChange={(v) => setPasswordData({ ...passwordData, mustChangePassword: v })}
              />
              <Label>Benutzer muss Passwort bei nächstem Login ändern</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={handleSetPassword} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Passwort ändern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Benutzer</TableHead>
                <TableHead>Rolle</TableHead>
                <TableHead>Abteilung</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Letzter Login</TableHead>
                <TableHead className="text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} className={!user.is_active ? 'opacity-50' : ''}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{user.full_name || user.username}</p>
                      <p className="text-sm text-gray-500">{user.username}</p>
                      {user.email && <p className="text-xs text-gray-400">{user.email}</p>}
                    </div>
                  </TableCell>
                  <TableCell>{getRoleBadge(user.role)}</TableCell>
                  <TableCell>{user.department_name || '-'}</TableCell>
                  <TableCell>
                    {user.is_active ? (
                      <Badge className="bg-green-100 text-green-700">Aktiv</Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-700">Inaktiv</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {user.last_login_at ? (
                        <>
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3 text-gray-400" />
                            <span>{formatDate(user.last_login_at)}</span>
                          </div>
                          {user.last_login_ip && (
                            <div className="flex items-center gap-1 text-xs text-gray-400">
                              <MapPin className="h-3 w-3" />
                              <span>{user.last_login_ip}</span>
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="text-gray-400">Nie</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    {isSuperAdmin() && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm"><MoreHorizontal className="h-4 w-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditDialog(user)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Bearbeiten
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openPasswordDialog(user)}>
                            <Key className="h-4 w-4 mr-2" />
                            Passwort ändern
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleResetPassword(user)}>
                            <Shield className="h-4 w-4 mr-2" />
                            Passwort zurücksetzen
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleToggleStatus(user)}>
                            {user.is_active ? (
                              <><UserX className="h-4 w-4 mr-2" />Deaktivieren</>
                            ) : (
                              <><UserCheck className="h-4 w-4 mr-2" />Aktivieren</>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleDelete(user)} className="text-red-600">
                            <Trash2 className="h-4 w-4 mr-2" />
                            Löschen
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default Users;
